import React, { useCallback, useEffect } from 'react';
import { FaceTabs } from '@uv-inserts/components/FaceTabs';
import { Visualiser, VisualiserGroup, VisualiserTextBox, VisualiserImageArea } from '@uv-inserts/components/Visualiser';
import PersonalisationSlotHeader from '../../PersonalisationSlotHeader';
import { FileInput } from '../../FileInput';
import { ToggleInput } from '../../ToggleInput';
import { usePersonalisationContext } from '../../../context/PersonalisationContext';
import { useLocale } from '../../../context/LocaleContext';
import { usePersonalisationUvInsertSlotViewModel } from './PersonalisationUvInsertSlot.vm';
import { buildUvInsertTextVisualiserSize } from '@shared/view/uv-inserts/build-text-visualiser-size';
import styles from './personalisation-uv-insert-slot.module.css';

/**
 * Converts a number or string dimension value to a CSS string.
 * Required because UVInsertConfig stores sizing as numbers (e.g. 200) or strings (e.g. "100%").
 * Always returns a string — falls back to the provided fallback or an empty string.
 *
 * @param {string|number|undefined} value - The value to convert.
 * @param {string} [fallback=''] - Fallback string if value is undefined.
 * @returns {string} The CSS string value.
 */
function toCssDimension(value, fallback = '') {
  if (value === undefined) return fallback;
  return String(value);
}

/**
 * PersonalisationUvInsertSlot renders the UV insert configuration UI within a personalisation step.
 *
 * Wires the existing shared UV insert components (FaceTabs, Visualiser, UvInsertChooser) into
 * the personalisation flow. All state is managed by PersonalisationUvInsertSlot.vm.ts and
 * synced back to PersonalisationContext via updateSelections on every change.
 *
 * Opt-in behaviour:
 * - Required slots: no toggle shown; insert is always included in choices (even if blank).
 * - Optional slots: toggle shown (default OFF); insert only included when toggled on.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.slot - The personalisation slot data (id, uiHeading, uvInsertConfigHandle, isRequired).
 * @returns {import('react').JSX.Element|null}
 */
export default function PersonalisationUvInsertSlot({ slot }) {
  const { resolvedUvInserts, updateSelections, selections, setActiveUvInsertChooser } = usePersonalisationContext();
  const { formatPrice } = useLocale();

  // Read the existing selection for this slot from context so the VM can restore state
  // when the user navigates back through the journey.
  const existingSelection = /** @type {import('./PersonalisationUvInsertSlot.vm').UvInsertSlotSelection | null} */ (
    selections?.slots.find((s) => s.id === slot.id)?.selected ?? null
  );

  /**
   * Syncs UV insert selection state (or null when opted out) to the personalisation context.
   *
   * @param {import('./PersonalisationUvInsertSlot.vm').UvInsertSlotSelection | null} selection - The current selection.
   */
  const handleSelectionChange = useCallback(
    (selection) => {
      updateSelections({ slotId: slot.id, itemSelected: selection });
    },
    [slot.id, updateSelections]
  );

  const vm = usePersonalisationUvInsertSlotViewModel({
    resolvedUvInserts,
    configHandle: slot.uvInsertConfigHandle,
    isRequired: slot.isRequired,
    uvInsertVisibleByDefault: slot.uvInsertVisibleByDefault,
    isFreeSlot: slot.isFreeSlot,
    overridePrice: slot.overridePrice,
    existingSelection,
    onSelectionChange: handleSelectionChange
  });

  useEffect(() => {
    if (!vm.optedIn || !vm.activeConfig || !resolvedUvInserts) {
      setActiveUvInsertChooser((current) => (current?.slotId === slot.id ? null : current));
      return undefined;
    }

    setActiveUvInsertChooser({
      slotId: slot.id,
      config: vm.activeConfig,
      tabIndex: vm.activeTabIndex,
      suggestedMessaging: resolvedUvInserts.suggestedMessaging,
      selectedThemeIndex: vm.themeIndex,
      onThemeIndexChange: vm.setThemeIndex,
      themeChooserOpen: vm.themeChooserOpen,
      onThemeChooserOpenChange: vm.setThemeChooserOpen,
      selectedSuggestionGroupIndex: vm.selectedSuggestionGroupIndex,
      onSelectedSuggestionGroupIndexChange: vm.setSelectedSuggestionGroupIndex,
      suggestionChooserOpen: vm.suggestionChooserOpen,
      onSuggestionChooserOpenChange: vm.setSuggestionChooserOpen,
      onSuggestionPick: vm.onSuggestionPick
    });

    return () => {
      setActiveUvInsertChooser((current) => (current?.slotId === slot.id ? null : current));
    };
  }, [
    resolvedUvInserts,
    setActiveUvInsertChooser,
    slot.id,
    vm.activeConfig,
    vm.activeTabIndex,
    vm.onSuggestionPick,
    vm.optedIn,
    vm.selectedSuggestionGroupIndex,
    vm.setSelectedSuggestionGroupIndex,
    vm.setSuggestionChooserOpen,
    vm.setThemeChooserOpen,
    vm.setThemeIndex,
    vm.suggestionChooserOpen,
    vm.themeChooserOpen,
    vm.themeIndex
  ]);

  // Data not yet loaded from bootstrap — render nothing until context is ready.
  if (!resolvedUvInserts) return null;

  // The config handle on this slot doesn't match any resolved UV insert config.
  if (!vm.activeConfig) {
    return (
      <div>
        <PersonalisationSlotHeader slot={slot} processHeading />
        <p>UV insert config &ldquo;{slot.uvInsertConfigHandle}&rdquo; not found. Check the slot configuration.</p>
      </div>
    );
  }

  return (
    <div>
      {/*
        Required slots show the standard slot header (heading + optional help link).
        Optional slots use the ToggleInput as their heading row — showing the slot header
        separately would double-display uiHeading.
      */}
      {vm.isSlotRequired && <PersonalisationSlotHeader slot={slot} processHeading />}

      {/* Price display for required slots (shown below the slot header). */}
      {vm.isSlotRequired && vm.effectivePrice !== null && (
        <p className={styles['personalisation__uv-insert-price'] + ' body-xs'}>
          {vm.originalPrice !== null && <s>{formatPrice({ amount: vm.originalPrice })}</s>}{' '}
          {vm.effectivePrice === 0 ? 'Free' : `+${formatPrice({ amount: vm.effectivePrice })}`}
        </p>
      )}
      <div className={styles['personalisation__uv-insert']}>
        {/* Optional slots: toggle with price inline in the label row. Required slots are always opted in. */}
        {!vm.isSlotRequired &&
          (() => {
            const title = slot.uvInsertPageId?.title || 'Add an insert';
            const priceText =
              vm.effectivePrice === null
                ? ''
                : vm.effectivePrice === 0
                  ? ' Free'
                  : ` +${formatPrice({ amount: vm.effectivePrice })}`;

            return (
              <ToggleInput
                id={`uv-insert-toggle-${slot.id}`}
                checked={vm.optedIn}
                onChange={vm.setOptedIn}
                description={slot.uvInsertPageId?.body}
                ariaLabel={`${title}${priceText}`}
                label={
                  <>
                    {title}
                    {vm.effectivePrice !== null && (
                      <span>
                        &nbsp;
                        {vm.originalPrice !== null && <s>{formatPrice({ amount: vm.originalPrice })}</s>}
                        {vm.effectivePrice === 0 ? ' Free' : `+${formatPrice({ amount: vm.effectivePrice })}`}
                      </span>
                    )}
                  </>
                }
              />
            );
          })()}

        {/* Only render the insert UI when the user has opted in (or the slot is required). */}
        {vm.optedIn && (
          <>
            <div className={styles['personalisation__uv-insert-container']}>
              {vm.faceTabItems.length > 1 && (
                <FaceTabs
                  tabs={vm.faceTabItems}
                  activeIndex={vm.activeTabIndex}
                  onActiveIndexChange={vm.setActiveTabIndex}
                  fullWidth
                />
              )}

              <div
                role="tabpanel"
                id={`uv-insert-panel-${slot.id}-${vm.activeTabIndex}`}
                aria-labelledby={`uv-insert-tab-${slot.id}-${vm.activeTabIndex}`}
                className={styles['personalisation__uv-insert-tab-panel']}
              >
                <VisualiserGroup>
                  {vm.facesToShow.map((face, fi) => {
                    // Only show the selected theme background image on the FRONT face when multiple themes exist.
                    const frontThemeBackground =
                      face.location === 'FRONT' && vm.selectedThemeImageSrc
                        ? vm.selectedThemeImageSrc
                        : null;

                    return (
                      <Visualiser
                        key={`${face.location}-${fi}`}
                        type={face.type}
                        background={face.backgroundColor}
                        backgroundImageUrl={frontThemeBackground ?? undefined}
                        subtitle={face.subtitle}
                      >
                        {face.elementConfig.map((el, ei) => {
                          if (el.type === 'TEXT') {
                            const key = (el.lineItemPropertyKey ?? '').trim();
                            if (el.editable === false) {
                              if (!key) {
                                return (
                                  <VisualiserTextBox
                                    key={`static-${face.location}-${fi}-${ei}`}
                                    font={el.font}
                                    fontWeight={el.fontWeight}
                                    fontSize={el.fontSize}
                                    lineHeight={el.lineHeight}
                                    color={el.color}
                                    maxLength={el.maxLength}
                                    maxLines={el.maxLines}
                                    size={buildUvInsertTextVisualiserSize(el.size)}
                                    position={{
                                      top: toCssDimension(el.positioning?.top),
                                      left: toCssDimension(el.positioning?.left),
                                      right: toCssDimension(el.positioning?.right),
                                      bottom: toCssDimension(el.positioning?.bottom)
                                    }}
                                    style={el.style}
                                    userEditable={false}
                                    value={el.value ?? ''}
                                    setValue={() => {}}
                                  />
                                );
                              }
                              const storedReadOnly = vm.textByKey[key];
                              const readOnlyDisplay =
                                storedReadOnly != null && storedReadOnly !== ''
                                  ? storedReadOnly
                                  : (el.value ?? '');
                              return (
                                <VisualiserTextBox
                                  key={`${key}-${ei}`}
                                  font={el.font}
                                  fontWeight={el.fontWeight}
                                  fontSize={el.fontSize}
                                  lineHeight={el.lineHeight}
                                  color={el.color}
                                  maxLength={el.maxLength}
                                  maxLines={el.maxLines}
                                  size={buildUvInsertTextVisualiserSize(el.size)}
                                  position={{
                                    top: toCssDimension(el.positioning?.top),
                                    left: toCssDimension(el.positioning?.left),
                                    right: toCssDimension(el.positioning?.right),
                                    bottom: toCssDimension(el.positioning?.bottom)
                                  }}
                                  style={el.style}
                                  userEditable={false}
                                  value={readOnlyDisplay}
                                  setValue={(v) => vm.setText(key, v)}
                                />
                              );
                            }
                            return (
                              <VisualiserTextBox
                                key={`${key}-${ei}`}
                                font={el.font}
                                fontWeight={el.fontWeight}
                                fontSize={el.fontSize}
                                lineHeight={el.lineHeight}
                                color={el.color}
                                maxLength={el.maxLength}
                                maxLines={el.maxLines}
                                size={buildUvInsertTextVisualiserSize(el.size)}
                                position={{
                                  top: toCssDimension(el.positioning?.top),
                                  left: toCssDimension(el.positioning?.left),
                                  right: toCssDimension(el.positioning?.right),
                                  bottom: toCssDimension(el.positioning?.bottom)
                                }}
                                style={el.style}
                                value={vm.textByKey[key] ?? ''}
                                setValue={(v) => vm.setText(key, v)}
                              />
                            );
                          }

                          if (el.type === 'IMAGE_UPLOAD') {
                            const key = el.lineItemPropertyKey;
                            const file = vm.imageByKey[key] ?? null;
                            const transform = vm.imageTransformByKey[key] ?? { scale: 1, x: 0, y: 0 };
                            return (
                              <VisualiserImageArea
                                key={`${key}-${ei}`}
                                file={file}
                                backgroundColor={el.backgroundColor}
                                size={{
                                  width: toCssDimension(el.size?.width, '200px'),
                                  height: toCssDimension(el.size?.height, '200px')
                                }}
                                position={{
                                  top: toCssDimension(el.positioning?.top),
                                  left: toCssDimension(el.positioning?.left)
                                }}
                                borderRadius={el.borderRadius}
                                style={el.style}
                                scale={transform.scale}
                                x={transform.x}
                                y={transform.y}
                                onUpdate={(updates) => {
                                  vm.setImageTransformByKey((prev) => ({
                                    ...prev,
                                    [key]: {
                                      scale: updates.scale ?? transform.scale,
                                      x: updates.x ?? transform.x,
                                      y: updates.y ?? transform.y
                                    }
                                  }));
                                }}
                              />
                            );
                          }

                          return null;
                        })}
                      </Visualiser>
                    );
                  })}
                </VisualiserGroup>
              </div>

              {vm.imageUploadKeysForActiveTab.length > 0 && (
                <div className={styles['personalisation__uv-insert-file-upload-container']}>
                  {vm.imageUploadKeysForActiveTab.map((imageKey) => (
                    <FileInput
                      key={imageKey}
                      label={vm.imageUploadKeysForActiveTab.length === 1 ? 'Upload photo' : `Upload (${imageKey})`}
                      onFileSelect={(value) => {
                        const file =
                          value.userImage !== null && value.userImage.file instanceof File
                            ? value.userImage.file
                            : null;
                        vm.setImageByKey((prev) => ({ ...prev, [imageKey]: file }));
                      }}
                      accept="png,jpg,jpeg,webp"
                      disabled={false}
                      buttonVariant="primary"
                      buttonColor="orange"
                      buttonSize="medium"
                      maxFileSize={5 * 1024 * 1024}
                      id={`uv-insert-img-${slot.id}-${imageKey}`}
                      selectedFile={vm.imageByKey[imageKey] ?? null}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
